import React, { useEffect, useRef } from "react";
import { useParams } from "react-router-dom";

import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Stack, Typography } from "@mui/material";
import {
  DriftMonitoring,
  MainTabs,
  PlannedDealsSidebar,
  PortfolioConstructionProvider,
  SliceSidebar,
  usePortfolioConstructionContext
} from "components/Portfolio-construction";
import { useNavigate } from "hooks";
import { MainTabsValue } from "store/types";

export const PortfolioConstruction: React.FC = () => (
  <PortfolioConstructionProvider>
    <Layout />
  </PortfolioConstructionProvider>
);

const Layout: React.FC = () => {
  const { selectedGroupId, selectedInvestorId, activeSliceType, investors, groups } = usePortfolioConstructionContext();

  const investorNotFound =
    activeSliceType === "investor" &&
    selectedInvestorId !== null &&
    !investors.some((i) => i.id === selectedInvestorId);

  const sliceNotFound =
    activeSliceType === "group" && selectedGroupId !== null && !groups.some((g) => g.id === selectedGroupId);

  const noSelection = (selectedGroupId === null && selectedInvestorId === null) || investorNotFound || sliceNotFound;

  useRouteSelectionSync();
  const { mainTab, setMainTab } = useMainTabRoute();

  return (
    <Stack gap="16px" sx={{ width: "100%", height: "100%", overflow: "hidden" }}>
      <MainTabs value={mainTab} onChange={setMainTab} />
      <Stack direction="row" gap="16px" sx={{ width: "100%", flex: "1 1 0", minHeight: 0, overflow: "hidden" }}>
        <Stack
          sx={{
            width: "320px",
            flexShrink: 0,
            height: "100%",
            gap: "16px",
            minHeight: 0
          }}
        >
          <SliceSidebar />
          <PlannedDealsSidebar />
        </Stack>
        <Stack sx={{ flex: 1, minWidth: 0, height: "100%", gap: "16px", overflow: "hidden" }}>
          <Stack sx={{ flex: "1 1 0", minHeight: 0, gap: "16px", overflowY: "auto" }}>
            {noSelection ? <EmptySelectionState /> : mainTab === "drift-monitoring" && <DriftMonitoring />}
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  );
};

const EmptySelectionState: React.FC = () => (
  <Stack
    alignItems="center"
    justifyContent="center"
    gap="10px"
    sx={{
      flex: "1 1 0",
      minHeight: 0,
      width: "100%",
      backgroundColor: "white",
      borderRadius: "8px",
      padding: "40px 16px",
      boxShadow: "0px 1px 2px 0px rgba(18, 18, 23, 0.1)"
    }}
  >
    <InfoOutlinedIcon sx={{ fontSize: 32, color: "#92a7bc" }} />
    <Typography sx={{ fontSize: "16px", fontWeight: 500, color: "#071f35" }}>No investor or slice selected</Typography>
    <Typography sx={{ fontSize: "13px", lineHeight: "18px", color: "#496682", textAlign: "center", maxWidth: 360 }}>
      Pick an investor or a slice from the left sidebar to see drift or objectives.
    </Typography>
  </Stack>
);

const useRouteSelectionSync = () => {
  const params = useParams<{ investor_id?: string; slice_id?: string }>();
  const navigate = useNavigate();
  const {
    selectedInvestorId,
    selectedGroupId,
    setSelectedInvestorId,
    setSelectedGroupId,
    setSliceTab,
    activeSliceType
  } = usePortfolioConstructionContext();

  const urlInvestorId = params.investor_id;
  const urlSliceId = params.slice_id;
  const syncingFromUrlRef = useRef(true);

  // URL → state
  useEffect(() => {
    syncingFromUrlRef.current = true;
    if (urlInvestorId !== undefined) {
      const parsed = /^\d+$/.test(urlInvestorId) ? Number(urlInvestorId) : urlInvestorId;
      setSelectedInvestorId(parsed);
      setSliceTab("investors");
    } else if (urlSliceId !== undefined) {
      const parsed = /^\d+$/.test(urlSliceId) ? Number(urlSliceId) : urlSliceId;
      setSelectedGroupId(parsed);
      setSliceTab("slice");
    } else {
      setSelectedInvestorId(null);
      setSelectedGroupId(null);
    }
  }, [urlInvestorId, urlSliceId, setSelectedInvestorId, setSelectedGroupId, setSliceTab]);

  // state → URL — skips the tick right after a URL→state sync to avoid clobbering the URL
  useEffect(() => {
    if (syncingFromUrlRef.current) {
      syncingFromUrlRef.current = false;
      return;
    }
    if (activeSliceType === "investor" && selectedInvestorId !== null) {
      if (String(selectedInvestorId) === urlInvestorId) return;
      navigate({
        route: "portfolioConstructionInvestor",
        params: { investor_id: String(selectedInvestorId) },
        redirectMethod: "replace"
      });
    } else if (activeSliceType === "group" && selectedGroupId !== null) {
      if (String(selectedGroupId) === urlSliceId) return;
      navigate({
        route: "portfolioConstructionSlice",
        params: { slice_id: String(selectedGroupId) },
        redirectMethod: "replace"
      });
    } else if (urlInvestorId !== undefined || urlSliceId !== undefined) {
      navigate({ route: "portfolioConstruction", params: {}, redirectMethod: "replace" });
    }
  }, [activeSliceType, selectedInvestorId, selectedGroupId, urlInvestorId, urlSliceId, navigate]);
};

const useMainTabRoute = (): { mainTab: MainTabsValue; setMainTab: (next: MainTabsValue) => void } => {
  return { mainTab: "drift-monitoring", setMainTab: () => undefined };
};
